"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";

/**
 * Live state the bottom tab bar shares with what floats above it.
 *
 * The bar's GEOMETRY is CSS (`--bottom-nav-*` on `:root` in globals.css), so a
 * floating button can sit on it with no JS at all. What CSS cannot see is the
 * bar's scroll-collapse — it shrinks from `--bottom-nav-height` to
 * `--bottom-nav-collapsed-height` while the reader scrolls down — so the bar
 * publishes that here and floaters translate by the difference, on the bar's
 * own duration and easing.
 *
 * Module state, not context: the bar is rendered by the layout, the floaters by
 * pages, and Next keeps navigated-away routes mounted inside hidden <Activity>
 * boundaries. Activity runs effect cleanups on hide, so a hidden bar retracts
 * its value (see `usePublishBottomNavShrunk`) and the visible one re-publishes
 * on reveal — never a DOM query that could match a hidden copy.
 */

let shrunk = false;
const listeners = new Set<() => void>();

function setShrunk(next: boolean) {
  if (shrunk === next) return;
  shrunk = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => shrunk;
const getServerSnapshot = () => false;

/** Whether the tab bar is currently scroll-collapsed. */
export function useBottomNavShrunk(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Called by the tab bar only. */
export function usePublishBottomNavShrunk(value: boolean) {
  useEffect(() => {
    setShrunk(value);
  }, [value]);

  useEffect(
    () => () => {
      setShrunk(false);
    },
    [],
  );
}

/**
 * Re-tapping the tab you are already on, at the top of its root page: the
 * iOS convention is "refresh". The tab bar handles the scroll-to-top itself
 * and only announces this second tap, so a page that ALREADY has a refresh
 * (the feed's pull-to-refresh) can run it. Pages without one ignore it.
 */
const TAB_RESELECT_AT_TOP_EVENT = "hiive:tab-reselect-at-top";

export function announceTabReselectAtTop(href: string) {
  window.dispatchEvent(
    new CustomEvent<{ href: string }>(TAB_RESELECT_AT_TOP_EVENT, {
      detail: { href },
    }),
  );
}

/**
 * Runs `handler` when the tab for `href` is re-tapped while its page is
 * already at the top. Lives in an effect, so a page Next is holding hidden in
 * <Activity> is unsubscribed until it is shown again.
 */
export function useTabReselectAtTop(href: string, handler: () => void) {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const onReselect = (event: Event) => {
      const detail = (event as CustomEvent<{ href?: string }>).detail;
      if (detail?.href === href) handlerRef.current();
    };
    window.addEventListener(TAB_RESELECT_AT_TOP_EVENT, onReselect);
    return () =>
      window.removeEventListener(TAB_RESELECT_AT_TOP_EVENT, onReselect);
  }, [href]);
}
